import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Container,
  Typography,
  IconButton,
  CircularProgress,
  Chip,
} from "@mui/material";
import { ArrowBack, Description, Download, InsertDriveFile } from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import { Toast } from "../components/Toast";
import { useToast } from "../hooks/useToast";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors, typography } from "../theme/designSystem";
import { workflowErrorMessage } from "../services/workflowsService";
import {
  documentLibraryService,
  downloadBlob,
  formatFileSize,
  DocumentLibraryDetail,
  PlaybookDocumentAttachment,
} from "../services/playbookDocumentsService";

const formatDate = (value: string | null): string =>
  value ? new Date(value).toLocaleDateString("pt-BR") : "-";

const DocumentView: React.FC = () => {
  const { navigateTo, getParam } = useRouter();
  const { user } = useAuth();
  const { toast, showToast, hideToast } = useToast();
  const documentId = getParam("documentId") || "";

  const [detail, setDetail] = useState<DocumentLibraryDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!documentId) {
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const data = await documentLibraryService.getById(documentId);
        setDetail(data);
        document.title = `Clinic4Us - ${data.title}`;
      } catch (e) {
        setDetail(null);
        showToast(workflowErrorMessage(e, "Erro ao carregar o documento."), "error");
      } finally {
        setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId]);

  const goToList = () => navigateTo("document-library");

  const download = async (attachment: PlaybookDocumentAttachment) => {
    try {
      const blob = await documentLibraryService.downloadAttachment(attachment.id);
      downloadBlob(blob, attachment.fileName);
    } catch (e) {
      showToast(workflowErrorMessage(e, "Erro ao baixar o arquivo."), "error");
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box
        component="main"
        className="c4u-main"
      >
        <Container maxWidth={false} disableGutters>
          <Box
            sx={{
              display: "flex",
              // Ações ficam na linha do título, inclusive no celular (RN-539h): com "wrap"
              // o bloco do título quebrava antes de encolher e as ações caíam para baixo.
              flexWrap: "nowrap",
              justifyContent: "space-between",
              alignItems: "flex-start",
              mb: 1,
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, minWidth: 0 }}>
              <IconButton onClick={goToList} title="Voltar para a Biblioteca" sx={{ mt: "-4px" }}>
                <ArrowBack />
              </IconButton>
              <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: "1.3rem",
                      fontWeight: typography.fontWeight.semibold,
                      color: colors.textPrimary,
                      wordBreak: "break-word",
                    }}
                  >
                    {detail?.title || "Documento"}
                  </Typography>
                  {detail?.category && (
                    <Chip
                      label={detail.category}
                      size="small"
                      variant="outlined"
                      sx={{ height: 22, fontSize: "0.7rem" }}
                    />
                  )}
                  {detail?.groupName && (
                    <Chip
                      label={detail.groupName}
                      size="small"
                      variant="outlined"
                      sx={{ height: 22, fontSize: "0.7rem" }}
                    />
                  )}
                </Box>
                <Typography
                  variant="body2"
                  sx={{ fontSize: typography.fontSize.sm, color: colors.textSecondary, pb: "15px" }}
                >
                  {detail?.summary || "Material de consulta da equipe."}
                  {detail
                    ? ` · Atualizado em ${formatDate(detail.updatedAt ?? detail.createdAt)}`
                    : ""}
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexShrink: 0,
                justifyContent: "flex-end",
              }}
            >
              <FaqButton />
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: colors.primary }} />
            </Box>
          ) : !detail ? (
            <Box sx={{ textAlign: "center", py: 6, color: colors.textSecondary }}>
              <Description sx={{ fontSize: 48, color: "#ced4da", mb: 1 }} />
              <Typography variant="body1" sx={{ mb: 0.5, color: colors.textPrimary }}>
                Documento não encontrado
              </Typography>
              <Typography variant="body2">
                Ele pode ter saído do ar ou não estar liberado para o seu perfil.
              </Typography>
            </Box>
          ) : (
            <>
              {detail.videoUrl && (
                <Paper
                  elevation={0}
                  sx={{ p: 1.5, mb: 2, border: "1px solid #e9ecef", borderRadius: "8px" }}
                >
                  <Typography sx={{ fontWeight: 600, color: colors.textPrimary, mb: 1 }}>
                    Vídeo
                  </Typography>
                  <Box
                    sx={{
                      width: "100%",
                      maxWidth: 760,
                      aspectRatio: "16 / 9",
                      borderRadius: "8px",
                      overflow: "hidden",
                      backgroundColor: "#000",
                    }}
                  >
                    <iframe
                      src={detail.videoUrl}
                      title={`Vídeo do documento ${detail.title}`}
                      allowFullScreen
                      // O YouTube valida a origem pelo Referer: com no-referrer o player recusa
                      // com "erro 153". strict-origin manda só o domínio, sem caminho nem query.
                      referrerPolicy="strict-origin-when-cross-origin"
                      allow="accelerometer; clipboard-write; encrypted-media; picture-in-picture; fullscreen"
                      style={{ width: "100%", height: "100%", border: 0, display: "block" }}
                    />
                  </Box>
                </Paper>
              )}

              <Paper
                elevation={0}
                sx={{
                  border: "1px solid #e9ecef",
                  borderRadius: "8px",
                  p: 1.5,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Typography sx={{ fontWeight: 600, color: colors.textPrimary }}>
                  Arquivos
                </Typography>
                {detail.attachments.length === 0 ? (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 3,
                      color: colors.textSecondary,
                      border: "1px dashed #ced4da",
                      borderRadius: "8px",
                    }}
                  >
                    <InsertDriveFile sx={{ fontSize: 36, color: "#ced4da", mb: 0.5 }} />
                    <Typography variant="body2" sx={{ color: colors.textPrimary }}>
                      Nenhum arquivo neste documento
                    </Typography>
                    <Typography variant="caption">
                      {detail.videoUrl
                        ? "O conteúdo deste material está no vídeo acima."
                        : "Os arquivos aparecem aqui quando forem enviados."}
                    </Typography>
                  </Box>
                ) : (
                  detail.attachments.map((attachment) => (
                    <Box
                      key={attachment.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        p: 1,
                        border: "1px solid #e9ecef",
                        borderRadius: "8px",
                      }}
                    >
                      <InsertDriveFile sx={{ color: colors.textSecondary, flexShrink: 0 }} />
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{ color: colors.textPrimary, wordBreak: "break-word", lineHeight: 1.25 }}
                        >
                          {attachment.fileName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: colors.textSecondary }}>
                          {formatFileSize(attachment.sizeBytes)} · enviado em{" "}
                          {formatDate(attachment.createdAt)}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={() => download(attachment)}
                        title="Baixar"
                        sx={{ color: colors.primary, flexShrink: 0 }}
                      >
                        <Download sx={{ fontSize: "1.1rem" }} />
                      </IconButton>
                    </Box>
                  ))
                )}
              </Paper>
            </>
          )}
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
      <Toast message={toast.message} type={toast.type} isVisible={toast.isVisible} onClose={hideToast} />
    </Box>
  );
};

export default DocumentView;
